import { datadogRum } from '@datadog/browser-rum';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { prefetchLazyChunksAfterFirstPaint } from 'src/lib/prefetch-lazy-chunks';
import { buildRouteChildren } from 'src/lib/routing/route-tree';
import { routes } from 'src/routes';

import { RouteError } from 'components/RouteError/RouteError';
import { RoutePending } from 'components/RoutePending/RoutePending';

import { getUrqlClient } from './network/client';
import { routeTree } from './routeTree.gen';

import type { AnyRoute } from '@tanstack/react-router';

/**
 * Graft the shim tree onto the generated (file-based) root as runtime-only
 * children, preserving the codegen's file-based children. The graft goes
 * through `AnyRoute` on purpose: the registered router type must stay the
 * clean generated tree so file-based routes get fully typed `Link` /
 * `useNavigate` / loaders. Shim routes are invisible to that typed surface —
 * their call sites navigate by raw `href` through `src/lib/routing`, so the
 * types never constrain them.
 */
const genChildren: AnyRoute[] = Array.isArray(routeTree.children)
  ? (routeTree.children as AnyRoute[])
  : Object.values((routeTree.children ?? {}) as Record<string, AnyRoute>);
(routeTree as AnyRoute).addChildren([
  ...genChildren,
  ...buildRouteChildren(routes, routeTree, { isFirstLoad: () => router.state.resolvedLocation === undefined }),
]);

export const router = createRouter({
  routeTree,
  // Getters, not instances — see `src/lib/routing/router-context.ts`.
  context: { getUrqlClient },
  // Match React Router defaults: no preloading, no auto scroll restoration.
  defaultPreload: false,
  scrollRestoration: false,
  // The shim config provides its own catch-all (`path: '*'`) routes.
  defaultNotFoundComponent: () => null,
  // Also wraps each match in a Suspense boundary, so a lazy route component shows this in its own slot.
  defaultPendingComponent: RoutePending,
  // Per-match error boundaries: the router only wraps a match when an
  // `errorComponent` is set, and `defaultOnCatch` reports what they catch.
  defaultErrorComponent: RouteError,
  defaultOnCatch: (error, errorInfo) => {
    datadogRum.addError(error, {
      source: 'route',
      type: 'error-boundary',
      errorInfo: errorInfo.componentStack,
    });
    datadogRum.startSessionReplayRecording();
  },
});

const unsubscribeFirstLoad = router.subscribe('onResolved', () => {
  unsubscribeFirstLoad();
  prefetchLazyChunksAfterFirstPaint();
});

declare module '@tanstack/react-router' {
  // oxlint-disable-next-line typescript/consistent-type-definitions -- declaration merging: `type` would not augment the ambient declaration
  interface Register {
    router: typeof router;
  }
}

export default function AppRoutes() {
  return <RouterProvider router={router} />;
}
